import React from 'react';
import {useTranslation} from 'react-i18next';
import cx from 'classnames';

import type {
    TooltipPosition,
    NotificationTooltipPosition,
} from '@pexip/components';
import {Box, IconTypes, NotificationTooltip} from '@pexip/components';

import {TestId} from '../../../../test/testIds';

import {FoldedSelfviewButton} from './FoldedSelfviewButton.view';

import styles from './FoldedSelfview.module.scss';

export const FoldedSelfview: React.FC<{
    isAudioInputMuted: boolean;
    isVideoInputMuted: boolean;
    isAudioInputMissing?: boolean;
    isVideoInputMissing?: boolean;
    isAudioInputDisabled?: boolean;
    isMobileDevice?: boolean;
    onExpandSelfview: () => void;
    onToggleVideoClick: () => void;
    onToggleAudioClick: () => void;
    showSelfviewTooltip: boolean;
    setShowSelfviewTooltip: (shouldShow: boolean) => void;
    notificationTooltipPos?: NotificationTooltipPosition;
    selfviewBtnTooltipPos?: TooltipPosition;
    expandSelfView: boolean;
}> = ({
    isAudioInputMuted,
    isVideoInputMuted,
    isAudioInputMissing,
    isVideoInputMissing,
    isAudioInputDisabled,
    isMobileDevice,
    // onToggleVideoClick,
    onExpandSelfview,
    // onToggleAudioClick,
    showSelfviewTooltip,
    setShowSelfviewTooltip,
    notificationTooltipPos = 'bottomEnd',
    selfviewBtnTooltipPos = 'topEnd',
    expandSelfView,
}) => {
    const {t} = useTranslation();

    const muteMicText = isAudioInputMuted
        ? t('media.unmute-microphone', 'Unmute microphone')
        : t('media.mute-microphone', 'Mute microphone');
    const showSelfviewText = t('meeting.show-self-view', 'Show self-view');
    const turnOnCameraText = t('media.turn-camera-on', 'Turn camera on');
    const dragText = t('meeting.folded-selfview.drag', 'Move self-view');

    const isAudioMutedOrMissing = isAudioInputMuted || isAudioInputMissing;

    return (
        <NotificationTooltip
            controlledIsVisible={
                showSelfviewTooltip &&
                !isVideoInputMuted &&
                !isVideoInputMissing
            }
            controlledOnChange={setShowSelfviewTooltip}
            header={t('settings.selfview-hidden', 'Self-view hidden')}
            position={notificationTooltipPos}
            testIdInner={TestId.NotificationTooltipSelfviewHidden}
            text={t(
                'settings.collapsed-selfview-tooltip-description',
                'You’re still sending video to other people in the meeting',
            )}
            closeButtonAriaLabel={t('common.close-tooltip', 'Close tooltip')}
            stopPointerDownPropagation
            className={styles.notificationTooltipInner}
        >
            <Box
                className={cx(styles.foldedSelfview, 'foldedSelfviewGlobal')}
                data-testid={TestId.FoldedSelfview}
            >
                <FoldedSelfviewButton
                    data-testid={TestId.ButtonDragFoldedSelfview}
                    icon={IconTypes.IconDraggable}
                    aria-label={dragText}
                    className={cx(styles.inheritCursor, styles.button)}
                    iconClassName={styles.buttonIcon}
                />

                {isAudioMutedOrMissing && (
                    <FoldedSelfviewButton
                        data-testid={TestId.ButtonToggleAudioInFoldedSelfview}
                        aria-label={muteMicText}
                        isDisabled={isAudioInputDisabled || isAudioInputMissing}
                        icon={
                            isAudioMutedOrMissing
                                ? IconTypes.IconMicrophoneOff
                                : IconTypes.IconMicrophoneOn
                        }
                        htmlTag="div"
                        noHover
                        className={styles.indicator}
                        iconClassName={styles.indicatorIcon}
                    />
                )}
                {(isVideoInputMuted || isVideoInputMissing) && (
                    <FoldedSelfviewButton
                        data-testid={TestId.ButtonToggleVideoInFoldedSelfview}
                        aria-label={turnOnCameraText}
                        icon={IconTypes.IconVideoOff}
                        isDisabled={isVideoInputMissing}
                        htmlTag="div"
                        noHover
                        className={styles.indicator}
                        iconClassName={styles.indicatorIcon}
                    />
                )}
                {!expandSelfView &&
                    !(isVideoInputMuted || isVideoInputMissing) && (
                        <FoldedSelfviewButton
                            onClick={onExpandSelfview}
                            data-testid={TestId.ButtonExpandSelfview}
                            tooltipText={
                                !isMobileDevice ? showSelfviewText : undefined
                            }
                            tooltipPosition={selfviewBtnTooltipPos}
                            aria-label={showSelfviewText}
                            icon={IconTypes.IconPasswordShow}
                            className={styles.button}
                            iconClassName={styles.buttonIcon}
                        />
                    )}
            </Box>
        </NotificationTooltip>
    );
};

export type FoldedSelfviewProps = React.ComponentProps<typeof FoldedSelfview>;
